/**
 * Exchange Rate Service
 *
 * Fetches and manages currency exchange rates using the European Central Bank (ECB) data
 * via the Frankfurter API (https://www.frankfurter.app/)
 *
 * Features:
 * - Free, no API key required
 * - Uses official ECB exchange rates
 * - Updates daily (ECB publishes rates around 4 PM CET)
 * - Supports USD, GBP, EUR, and 30+ other currencies
 * - Caching in localStorage and Firestore
 * - Offline fallback support
 */

import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Supported currencies with their details
export const SUPPORTED_CURRENCIES = {
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' }
};

// API endpoint - uses ECB data, free and reliable
const FRANKFURTER_API = 'https://api.frankfurter.app';

// Cache duration: 24 hours (rates update once per day)
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000;

// LocalStorage keys
const STORAGE_KEYS = {
  RATES: 'planlens_exchange_rates',
  LAST_UPDATE: 'planlens_rates_last_update'
};

/**
 * Fetch latest exchange rates from ECB via Frankfurter API
 * @returns {Promise<Object>} Rates object with base EUR
 */
const fetchRatesFromAPI = async () => {
  try {
    // Fetch rates with EUR as base (ECB standard)
    const response = await fetch(`${FRANKFURTER_API}/latest`);

    if (!response.ok) {
      throw new Error(`API responded with status: ${response.status}`);
    }

    const data = await response.json();

    // API returns: { amount: 1, base: "EUR", date: "2024-01-12", rates: { USD: 1.09, GBP: 0.86, ... } }
    return {
      base: data.base,
      date: data.date,
      rates: {
        ...data.rates,
        EUR: 1.0 // Include base currency
      },
      timestamp: Date.now()
    };
  } catch (error) {
    console.error('Error fetching exchange rates from API:', error);
    throw error;
  }
};

/**
 * Get cached rates from localStorage
 * @returns {Object|null} Cached rates or null if expired/not found
 */
const getCachedRates = () => {
  try {
    const cachedRates = localStorage.getItem(STORAGE_KEYS.RATES);
    const lastUpdate = localStorage.getItem(STORAGE_KEYS.LAST_UPDATE);

    if (!cachedRates || !lastUpdate) {
      return null;
    }

    const cacheAge = Date.now() - parseInt(lastUpdate, 10);

    // Return cached rates if less than 24 hours old
    if (cacheAge < CACHE_DURATION_MS) {
      const parsed = JSON.parse(cachedRates);
      // Validate that rates object has the expected structure
      if (parsed && parsed.rates && typeof parsed.rates === 'object') {
        console.log('📊 [Service] Valid cached rates found');
        return parsed;
      } else {
        console.warn('⚠️ [Service] Cached rates invalid, clearing cache');
        localStorage.removeItem(STORAGE_KEYS.RATES);
        localStorage.removeItem(STORAGE_KEYS.LAST_UPDATE);
        return null;
      }
    }

    return null;
  } catch (error) {
    console.error('Error reading cached rates:', error);
    // Clear corrupted cache
    localStorage.removeItem(STORAGE_KEYS.RATES);
    localStorage.removeItem(STORAGE_KEYS.LAST_UPDATE);
    return null;
  }
};

/**
 * Save rates to localStorage cache
 * @param {Object} rates - Rates object to cache
 */
const cacheRates = (rates) => {
  try {
    localStorage.setItem(STORAGE_KEYS.RATES, JSON.stringify(rates));
    localStorage.setItem(STORAGE_KEYS.LAST_UPDATE, Date.now().toString());
  } catch (error) {
    console.error('Error caching rates:', error);
  }
};

/**
 * Get rates from Firestore (shared across user's devices)
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Rates or null
 */
const getRatesFromFirestore = async (userId) => {
  try {
    if (!userId) return null;

    const docRef = doc(db, 'exchangeRates', userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const cacheAge = Date.now() - (data.timestamp || 0);

      // Return if less than 24 hours old
      if (cacheAge < CACHE_DURATION_MS) {
        return data;
      }
    }

    return null;
  } catch (error) {
    console.error('Error fetching rates from Firestore:', error);
    return null;
  }
};

/**
 * Save rates to Firestore
 * @param {string} userId - User ID
 * @param {Object} rates - Rates to save
 */
const saveRatesToFirestore = async (userId, rates) => {
  try {
    if (!userId) return;

    const docRef = doc(db, 'exchangeRates', userId);
    await setDoc(docRef, rates, { merge: true });
  } catch (error) {
    console.error('Error saving rates to Firestore:', error);
  }
};

/**
 * Get current exchange rates with multi-level caching
 * 1. Check localStorage (instant)
 * 2. Check Firestore (cross-device sync)
 * 3. Fetch from API (if cache expired)
 *
 * @param {string} userId - Optional user ID for Firestore caching
 * @returns {Promise<Object>} Exchange rates object
 */
export const getExchangeRates = async (userId = null) => {
  try {
    // Level 1: Check localStorage cache
    let rates = getCachedRates();
    if (rates) {
      console.log('📊 Using cached exchange rates from localStorage');
      return rates;
    }

    // Level 2: Check Firestore cache
    if (userId) {
      rates = await getRatesFromFirestore(userId);
      if (rates) {
        console.log('📊 Using exchange rates from Firestore');
        // Update localStorage for faster access next time
        cacheRates(rates);
        return rates;
      }
    }

    // Level 3: Fetch fresh rates from API
    console.log('📊 Fetching fresh exchange rates from ECB...');
    rates = await fetchRatesFromAPI();

    // Cache the fresh rates
    cacheRates(rates);
    if (userId) {
      await saveRatesToFirestore(userId, rates);
    }

    return rates;
  } catch (error) {
    console.error('Error getting exchange rates:', error);

    // Fallback: Use last known rates even if expired
    const cachedRates = localStorage.getItem(STORAGE_KEYS.RATES);
    if (cachedRates) {
      console.warn('⚠️ Using expired cached rates as fallback');
      return JSON.parse(cachedRates);
    }

    // Ultimate fallback: Hardcoded approximate rates
    console.warn('⚠️ Using hardcoded fallback rates');
    const fallbackRates = {
      base: 'EUR',
      date: new Date().toISOString().split('T')[0],
      rates: {
        USD: 1.09,
        GBP: 0.86,
        EUR: 1.0
      },
      timestamp: Date.now(),
      isFallback: true
    };
    console.log('📊 [Service] Returning fallback rates:', fallbackRates);
    return fallbackRates;
  }
};

/**
 * Convert amount from one currency to another
 *
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency code (USD, GBP, EUR)
 * @param {string} toCurrency - Target currency code (USD, GBP, EUR)
 * @param {Object} rates - Exchange rates object (from getExchangeRates)
 * @returns {number} Converted amount
 */
export const convertCurrency = (amount, fromCurrency, toCurrency, rates) => {
  // No conversion needed if same currency
  if (fromCurrency === toCurrency) {
    return amount;
  }

  if (!rates || !rates.rates) {
    console.error('Invalid rates object provided');
    return amount;
  }

  // All rates are relative to EUR (base)
  const fromRate = rates.rates[fromCurrency];
  const toRate = rates.rates[toCurrency];

  if (!fromRate || !toRate) {
    console.error(`Missing rate for ${fromCurrency} or ${toCurrency}`);
    return amount;
  }

  // Convert: amount (fromCurrency) -> EUR -> toCurrency
  const amountInEUR = amount / fromRate;
  const convertedAmount = amountInEUR * toRate;

  return convertedAmount;
};

/**
 * Format amount with currency
 *
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (USD, GBP, EUR)
 * @param {boolean} showCode - Whether to show currency code (default: false)
 * @returns {string} Formatted currency string
 */
export const formatCurrencyAmount = (amount, currency, showCode = false) => {
  const currencyInfo = SUPPORTED_CURRENCIES[currency];

  if (!currencyInfo) {
    return amount.toLocaleString();
  }

  const locale = currency === 'USD' ? 'en-US' : currency === 'GBP' ? 'en-GB' : 'de-DE';

  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);

  if (showCode) {
    return `${formatted} ${currency}`;
  }

  return formatted;
};

/**
 * Get exchange rate between two currencies
 *
 * @param {string} fromCurrency - Source currency
 * @param {string} toCurrency - Target currency
 * @param {Object} rates - Exchange rates object
 * @returns {number} Exchange rate
 */
export const getExchangeRate = (fromCurrency, toCurrency, rates) => {
  if (fromCurrency === toCurrency) {
    return 1.0;
  }

  if (!rates || !rates.rates) {
    return 1.0;
  }

  const fromRate = rates.rates[fromCurrency];
  const toRate = rates.rates[toCurrency];

  if (!fromRate || !toRate) {
    return 1.0;
  }

  // Rate to convert 1 unit of fromCurrency to toCurrency
  return toRate / fromRate;
};

/**
 * Force refresh exchange rates (bypass cache)
 *
 * @param {string} userId - Optional user ID
 * @returns {Promise<Object>} Fresh exchange rates
 */
export const refreshExchangeRates = async (userId = null) => {
  try {
    console.log('🔄 Forcing refresh of exchange rates...');
    const rates = await fetchRatesFromAPI();

    // Update all caches
    cacheRates(rates);
    if (userId) {
      await saveRatesToFirestore(userId, rates);
    }

    console.log('✅ Exchange rates refreshed successfully');
    return rates;
  } catch (error) {
    console.error('Error refreshing exchange rates:', error);
    throw error;
  }
};

/**
 * Get last update timestamp
 * @returns {number|null} Timestamp or null
 */
export const getLastUpdateTimestamp = () => {
  const lastUpdate = localStorage.getItem(STORAGE_KEYS.LAST_UPDATE);
  return lastUpdate ? parseInt(lastUpdate, 10) : null;
};

/**
 * Check if rates are stale (older than 24 hours)
 * @returns {boolean} True if rates need refresh
 */
export const areRatesStale = () => {
  const lastUpdate = getLastUpdateTimestamp();
  if (!lastUpdate) return true;

  const age = Date.now() - lastUpdate;
  return age >= CACHE_DURATION_MS;
};

export default {
  SUPPORTED_CURRENCIES,
  getExchangeRates,
  convertCurrency,
  formatCurrencyAmount,
  getExchangeRate,
  refreshExchangeRates,
  getLastUpdateTimestamp,
  areRatesStale
};
